import type { UIMessage } from '@ai-sdk/ui-utils';
import { z } from 'zod';

import type { ChatMessage, ChatPart } from '@core/tellent-assistant-types';

const MastraToolInvocationPartSchema = z.object({
  type: z.literal('tool-invocation'),
  toolInvocation: z.object({
    state: z.enum(['call', 'result', 'partial-call']),
    toolCallId: z.string(),
    toolName: z.string(),
    args: z.unknown(),
    result: z.unknown().optional(),
  }),
});

const MastraTextPartSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
  createdAt: z.number().optional(),
});

const MastraPartSchema = z.union([
  MastraToolInvocationPartSchema,
  MastraTextPartSchema,
  z.object({ type: z.string() }).passthrough(),
]);

const MastraMessageContentSchema = z.object({
  format: z.number().optional(),
  parts: z.array(MastraPartSchema).optional(),
  content: z.string().optional(),
});

const MastraMessageSchema = z.object({
  id: z.string().optional(),
  role: z.enum(['user', 'assistant', 'tool', 'system']),
  content: z.union([z.string(), MastraMessageContentSchema]),
  parts: z.array(MastraPartSchema).optional(),
  createdAt: z.string().optional(),
});

type MastraMessage = z.infer<typeof MastraMessageSchema>;

export const toChatMessages = (raw: readonly unknown[]): ChatMessage[] => {
  const out: ChatMessage[] = [];
  for (const item of raw) {
    const parsed = MastraMessageSchema.safeParse(item);
    if (!parsed.success) continue;
    const m = parsed.data;
    if (m.role !== 'user' && m.role !== 'assistant') continue;
    const id = m.id ?? crypto.randomUUID();
    const parts =
      m.parts && m.parts.length > 0
        ? extractParts({ parts: m.parts }, id)
        : extractParts(m.content, id);
    if (parts.length === 0) continue;
    out.push({
      id,
      role: m.role,
      parts,
      createdAt: m.createdAt ? new Date(m.createdAt) : new Date(),
    });
  }
  return out;
};

function extractParts(content: MastraMessage['content'], messageId: string): ChatPart[] {
  if (typeof content === 'string') {
    return content ? [{ kind: 'text', messageId, content, done: true }] : [];
  }

  const parts: ChatPart[] = [];

  for (const p of content.parts ?? []) {
    if (p.type === 'text') {
      const textPart = MastraTextPartSchema.safeParse(p);
      if (textPart.success && textPart.data.text) {
        parts.push({ kind: 'text', messageId, content: textPart.data.text, done: true });
      }
    } else if (p.type === 'tool-invocation') {
      const toolPart = MastraToolInvocationPartSchema.safeParse(p);
      if (!toolPart.success) continue;
      const inv = toolPart.data.toolInvocation;
      parts.push({
        kind: 'tool-call',
        toolCallId: inv.toolCallId,
        name: inv.toolName,
        args: inv.args,
        status: inv.state === 'result' ? 'done' : 'running',
        startedAt: 0,
      });
      if (inv.state === 'result') {
        parts.push({
          kind: 'tool-result',
          toolCallId: inv.toolCallId,
          name: inv.toolName,
          payload: inv.result,
        });
      }
    }
  }

  if (parts.length === 0 && content.content) {
    parts.push({ kind: 'text', messageId, content: content.content, done: true });
  }

  return parts;
}

export const userV2Message = (text: string): UIMessage => ({
  id: crypto.randomUUID(),
  role: 'user',
  content: text,
  parts: [{ type: 'text', text }],
});

export const chatMessagesToV2Messages = (msgs: readonly ChatMessage[]): UIMessage[] =>
  msgs.map(m => {
    type Part = NonNullable<UIMessage['parts']>[number];
    type ToolPart = Extract<Part, { type: 'tool-invocation' }>;
    const partsOut: Part[] = [];
    const toolByCallId = new Map<string, ToolPart>();

    for (const p of m.parts) {
      if (p.kind === 'text') {
        partsOut.push(<Part>{ type: 'text', text: p.content });
        continue;
      }
      if (p.kind === 'tool-call') {
        const existing = toolByCallId.get(p.toolCallId);
        if (existing) {
          existing.toolInvocation = {
            ...existing.toolInvocation,
            args: p.args,
            toolName: p.name,
          };
          continue;
        }
        const part = <ToolPart>{
          type: 'tool-invocation',
          toolInvocation: {
            state: 'call',
            toolCallId: p.toolCallId,
            toolName: p.name,
            args: p.args,
          },
        };
        toolByCallId.set(p.toolCallId, part);
        partsOut.push(part);
        continue;
      }
      if (p.kind === 'tool-result') {
        const existing = toolByCallId.get(p.toolCallId);
        if (existing) {
          existing.toolInvocation = {
            ...existing.toolInvocation,
            state: 'result',
            result: p.payload,
          };
          continue;
        }
        const part = <ToolPart>{
          type: 'tool-invocation',
          toolInvocation: {
            state: 'result',
            toolCallId: p.toolCallId,
            toolName: p.name,
            args: {},
            result: p.payload,
          },
        };
        toolByCallId.set(p.toolCallId, part);
        partsOut.push(part);
      }
    }

    return {
      id: m.id,
      role: m.role,
      content: m.parts
        .filter((p): p is Extract<ChatPart, { kind: 'text' }> => p.kind === 'text')
        .map(p => p.content)
        .join(''),
      parts: partsOut,
    };
  });
